import * as Sentry from '@sentry/nextjs';
import { AxiosRequestConfig, isAxiosError } from 'axios';
import { atom } from 'jotai';
import { RESET, selectAtom } from 'jotai/utils';
import { queryClientAtom } from 'jotai-tanstack-query';
import jwtDecode from 'jwt-decode';
import randomString from 'randomstring';
import appConfig from 'app-config';
import { RPC_ERROR_CODES } from 'src/apis/exception';
import { isDefinedRPCException } from 'src/apis/utils';
import AgeRestrictedModal from 'src/components/AgeRestrictedModal';
import AlertModal from 'src/components/AlertModal';
import EmailAddressMatchStep from 'src/components/AzarOnly/Login/Steps/Email/EmailAddressMatchStep';
import EmailAddressRegisterStep from 'src/components/AzarOnly/Login/Steps/Email/EmailAddressRegisterStep';
import EmailVerificationSelectStep from 'src/components/AzarOnly/Login/Steps/Email/EmailVerificationSelectStep';
import NotExistEmailStep from 'src/components/AzarOnly/Login/Steps/Email/NotExistEmailStep';
import CancelDeletionReservationModal from 'src/components/CancelDeletionReservationModal';
import ErrorAccountsModal from 'src/components/ErrorAccountsModal';
import InduceRegisterModal from 'src/components/InduceRegisterModal';
import LoginModal from 'src/components/LoginModal';
import SuspensionModal from 'src/components/SuspensionModal';
import VividiRegisterModal from 'src/components/VividiOnly/Register';
import { cleanupPushNotification } from 'src/push';
import { showNetworkErrorReloadModalAtom } from 'src/stores/error/atoms';
import { clearMatchAtom, clearSourceStreamAtom, initialMatchFilterSelections, isVideoObjectFitCoverAtom, matchFilterSelectionsAtom, statusAtom } from 'src/stores/match/atoms';
import { closeModalAtom, showModalAtom } from 'src/stores/modal/atoms';
import { handleNonKmcSelfErrorAtom, smsVerificationResponseAtom, emailVerificationAtom, pushPhoneNumberLoginHistoryAtom, phoneNumberLoginHistoryAtom } from 'src/stores/phoneNumberLogin/atoms';
import { LoginModalType, loginModalTypeAtom, purgeBeforeSignUpAtom, resetRegisterAtom } from 'src/stores/register/atoms';
import { closePaymentPopupAtom } from 'src/stores/shop/atoms';
import { accumulateSubscriptionHistoryAtom, subscriptionHistoryAtom } from 'src/stores/subscription/atoms';
import { showToastAtom } from 'src/stores/toast/atoms';
import { lastGetLegalUpdatesDateAtom, lastUserInfoUpdateDateAtom } from 'src/stores/user/atom';
import { FeatureSettingPopup } from 'src/stores/user/types';
import { getVipRatingAtom, vipRatingAtom } from 'src/stores/vip/atoms';
import { AzarUser, RemoteConfig } from 'src/types/AzarUser';
import { Consent } from 'src/types/consent';
import { EVENT_LOGIN_MODAL_VISITOR_TRIGGER, EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { InventoryItem, InventoryItemCategoryId } from 'src/types/itemInventory';
import { STATUS } from 'src/types/Match';
import { ModalType } from 'src/types/Modal';
import { GENDER, LoginType } from 'src/types/register';
import { ToastType } from 'src/types/Toast';
import { SocialToken } from 'src/types/Token';
import { rpcAxios, RPCResponse, RPCThrowableError, setDTID } from 'src/utils/api';
import { deepEqual } from 'src/utils/common';
import { ControlledPromise } from 'src/utils/controlled-promise';
import { pushEvent } from 'src/utils/event-gateway';
import { parseRawInventoryItems } from 'src/utils/itemInventory';
import { getAtomWithCookies, getAtomWithStorage, reviveDateFromStorage } from 'src/utils/localStorageUtils';
import { safeWindow } from 'src/utils/static-generation';
import { IceServerManager } from 'src/utils/webrtc/ice-server-manager';
import { acceptCmpConsentsAPI, getCmpConsentsAPI, getFeatureSettingsPopupAPI, getInventoryAPI, GetServersInfoAPI, loginEmailAPI, LoginEmailParams, loginOAuthAPI, LoginOAuthParams, loginWithPhoneNumberAPI, loginWithAccessTokenAPI, refreshAccessTokenAPI, registerEmailAPI, RegisterEmailParams, LoginResponse } from './apis';
export const isDeviceAgeRestrictedAtom = getAtomWithCookies<boolean>('isDeviceAgeRestrictedByAge', false);

/**
 * 회원가입 직후 최초 로그인일 경우에만 true로 설정 그 외 false
 */
export const isUserJustSignedUpAtom = atom(false);
interface RetryPromiseInfo {
  promise: ControlledPromise<void>;
  preventReLogin?: boolean;
}
export const userJoinDateAtom = getAtomWithStorage<Date | null>('userJoinDate', null, {
  reviver: reviveDateFromStorage
});
const retryPromiseAtom = atom<RetryPromiseInfo[]>([]);
const fireRetryPromiseAtom = atom(null, async (get, set, retryPromises: RetryPromiseInfo[] | void) => {
  try {
    await Promise.allSettled((retryPromises || get(retryPromiseAtom)).map(({
      promise
    }) => promise.resolve()));
  } catch (_e) {
    // discard error
  }
  set(retryPromiseAtom, []);
});
const isRetryingAtom = atom<boolean>(false);
export const addRetryPromiseAtom = atom(null, (get, set, retryPromiseInfo: RetryPromiseInfo) => {
  set(retryPromiseAtom, get(retryPromiseAtom).concat([retryPromiseInfo]));
});
export const userDataAtom = getAtomWithStorage<AzarUser | undefined>('userDataV2', undefined);
export const azarTokenAtom = selectAtom(userDataAtom, userData => {
  if (!userData) {
    return undefined;
  }
  const {
    accessToken,
    refreshToken
  } = userData;
  return {
    accessToken,
    refreshToken
  };
}, deepEqual);
export const debugRemoteConfigAtom = getAtomWithStorage<RemoteConfig | null>('debugRemoteConfig', null);
export const remoteConfigAtom = atom(get => {
  const userData = get(userDataAtom);
  if (!userData) return;
  return {
    ...userData?.remoteConfig,
    ...(get(debugRemoteConfigAtom) || {})
  };
});

/**
 * null인 경우 더 이상 처리할 약관이 없음
 * undefined인 경우 서버로부터 조회해봐야 하는 상태
 * 유저가 미응답 상태에서 페이지 이탈 후 바로 매치 돌리는 것 방지하기 위해 storage에 저장
 */
export const cmpConsentsAtom = getAtomWithStorage<Consent | undefined | null>('cmpConsentsPopup', undefined);
export const isGettingCmpConsentsAtom = atom(false);
export const getCmpConsentsAtom = atom(null, async (get, set) => {
  if (!get(azarTokenAtom)?.accessToken) return;
  if (get(isGettingCmpConsentsAtom)) return;
  if (!get(cmpConsentsAtom)) {
    try {
      set(isGettingCmpConsentsAtom, true);
      const response = await getCmpConsentsAPI();
      if (response.data.consent) {
        set(cmpConsentsAtom, response.data.consent);
      } else {
        set(cmpConsentsAtom, null);
      }
    } finally {
      set(isGettingCmpConsentsAtom, false);
    }
  }
});
export const acceptCmpConsentsAtom = atom(null, async (get, set) => {
  let code = get(cmpConsentsAtom)?.code;
  if (!code) {
    const response = await getCmpConsentsAPI();
    if (response.data.consent) {
      code = response.data.consent.code;
    }
  }
  if (code) {
    try {
      set(cmpConsentsAtom, null);
      await acceptCmpConsentsAPI(code);
    } catch (e) {
      return;
    }
  }
});
export const featureSettingsPopupsAtom = atom<FeatureSettingPopup[] | undefined>(undefined);
export const getFeatureSettingsPopupAtom = atom(null, async (get, set) => {
  if (!get(featureSettingsPopupsAtom) && get(azarTokenAtom)?.accessToken) {
    try {
      const response = await getFeatureSettingsPopupAPI();
      if (response.data.result.popups) {
        set(featureSettingsPopupsAtom, response.data.result.popups);
      }
    } catch (_e) {
      return;
    }
  }
});
export const isLoginLoadingAtom = atom<boolean>(false);
export const DTIDAtom = getAtomWithStorage<string | undefined>('X-Azar-DTIDV2', undefined);
export const setDTIDAtom = atom(null, (get, set) => {
  get(DTIDAtom);
  if (get(DTIDAtom)) {
    setDTID(get(DTIDAtom));
  } else {
    const dtid = randomString.generate({
      //브라우저 고유값(기기값과 비슷한기능, SMS 중복전송 차단 등에 활용
      length: 8,
      charset: 'hex'
    }).toUpperCase();
    set(DTIDAtom, dtid);
    setDTID(dtid);
  }
});
export const stompBrokerInfoAtom = getAtomWithStorage<AzarUser['stompBrokerInfo'] | void>('stompBrokerInfoV2', undefined);
export const userInventoryAtom = getAtomWithStorage<InventoryItem[]>('userInventoryV2', []);
export const quickSlotCategoryAtom = atom<InventoryItemCategoryId | null>(null);
export const lastInventoryUpdateTimeAtom = getAtomWithStorage<Date | null>('lastInventoryUpdateTime', null, {
  reviver: reviveDateFromStorage
});
export const removeTokenAtom = atom(null, async (get, set) => {
  if (get(statusAtom) >= STATUS.FINDING) {
    try {
      await set(clearMatchAtom);
    } catch (e) {
      //
    }
  }
  cleanupPushNotification();
  set(userDataAtom, undefined);
  set(stompBrokerInfoAtom, undefined);
  IceServerManager.getInstance().cleanIceServers();
  set(userInventoryAtom, []);
  set(subscriptionHistoryAtom, []);
  set(vipRatingAtom, null);
  set(matchFilterSelectionsAtom, initialMatchFilterSelections);
  set(isVideoObjectFitCoverAtom, true);
  set(cmpConsentsAtom, undefined);
  set(featureSettingsPopupsAtom, undefined);
  set(closePaymentPopupAtom);
  set(isUserJustSignedUpAtom, false);
  set(clearSourceStreamAtom);
  get(queryClientAtom).removeQueries({
    predicate({
      meta: {
        withoutAuth = false
      } = {}
    }) {
      return !withoutAuth;
    }
  });
});
const MIN_SET_SERVERS_INFO_INTERVAL_MS = 1000 * 10;
const lastSetServersInfoDateAtom = atom<Date | null>(null);
export const serverInfoPromiseAtom = atom<Promise<boolean> | null>(null);
export const hasFailedToSetServersInfoAtom = atom<boolean>(false);

/**
 * ICE, STOMP 브로커 등의 서버 정보들을 새롭게 받아와서 적용하는 함수
 * 실제로 새로운 정보를 받아와서 적용했는지 여부를 반환
 */
export const setServersInfoAtom = atom(null, async (get, set) => {
  const userData = get(userDataAtom);
  if (!userData) {
    return false;
  }
  const controlledPromise = new ControlledPromise<boolean>();
  try {
    const serverInfoPromise = get(serverInfoPromiseAtom);
    if (serverInfoPromise) {
      return await serverInfoPromise;
    }
    const lastSetServersInfoDate = get(lastSetServersInfoDateAtom);
    if (lastSetServersInfoDate && lastSetServersInfoDate.getTime() > Date.now() - MIN_SET_SERVERS_INFO_INTERVAL_MS) {
      return false;
    }
    set(lastSetServersInfoDateAtom, new Date());
    set(serverInfoPromiseAtom, controlledPromise.promise);
    const {
      data
    } = await GetServersInfoAPI();
    IceServerManager.getInstance().setIceServers(data.iceServers);
    set(stompBrokerInfoAtom, data.stompBrokerInfo);
    controlledPromise.resolve(true);
    set(hasFailedToSetServersInfoAtom, false);
    return true;
  } catch (e) {
    if (get(statusAtom) > STATUS.INITIAL) {
      set(showNetworkErrorReloadModalAtom);
      set(clearMatchAtom);
    }
    controlledPromise.resolve(false);
    set(hasFailedToSetServersInfoAtom, true);
    return false;
  } finally {
    set(serverInfoPromiseAtom, null);
  }
});
export const pushChannelNameAtom = selectAtom(userDataAtom, userData => userData?.pushChannelName, deepEqual);
export const userCountryCodeAtom = selectAtom(userDataAtom, userData => userData?.userProfile?.location?.countryCode, deepEqual);
export const tryLoginTypeAtom = atom<LoginType | undefined>(undefined);
export const socialTokenAtom = atom<SocialToken | undefined>(undefined);
export const getInventoryAtom = atom(null, async (get, set) => {
  if (!get(azarTokenAtom)) return;
  const response = await getInventoryAPI();
  const {
    data: {
      data
    }
  } = response;
  if (!data) {
    Sentry.captureMessage('GetInventory data not exist', {
      extra: {
        response
      }
    });
    return;
  }
  const newInventory = parseRawInventoryItems(data);
  set(userInventoryAtom, newInventory);

  /** 아이템 만료 여부와 무관하게 쌓아감. */
  set(accumulateSubscriptionHistoryAtom, newInventory);
});
interface LoginRetryData {
  config: AxiosRequestConfig;
}
export const loginRetryDataAtom = atom<LoginRetryData | null>(null);
export const retryWithSmsVerificationAtom = atom(null, (get, set, params: LoginRetryData) => {
  set(loginModalTypeAtom, LoginModalType.SOCIAL_LOGIN);
  set(loginRetryDataAtom, params);
});
interface Token {
  exp: number;
}
export const renewAccessTokenAtom = atom(null, async (get, set) => {
  if (get(isRetryingAtom)) {
    return;
  }
  set(isRetryingAtom, true);
  const userData = get(userDataAtom);
  const refreshToken = get(azarTokenAtom)?.refreshToken;
  if (!userData || !refreshToken) {
    set(removeTokenAtom);
    return;
  }
  try {
    const response = await refreshAccessTokenAPI(refreshToken);
    set(userDataAtom, {
      ...userData,
      ...response.data
    });
    set(fireRetryPromiseAtom);
  } catch (error) {
    // Response status가 401이면 code와 무관하게 refresh token이 invalid한 것을 판단하고 로그아웃 처리
    if (isAxiosError(error) && error.response?.status === 401) {
      set(removeTokenAtom);
      if (window.AzarJs) {
        Sentry.captureMessage(`Token has been expired or revoked. ${error}, ${userData.userId}, ${userData.accessToken}, ${refreshToken}`);
        alert('Sorry. Please Login again');
        window.AzarJs.closePopup();
      } else {
        const retryPromises = get(retryPromiseAtom);
        set(retryPromiseAtom, []);
        if (retryPromises.every(({
          preventReLogin
        }) => !preventReLogin)) {
          set(showModalAtom, {
            key: ModalType.LOGIN,
            component: () => LoginModal({
              loginSuccessCallback: () => set(fireRetryPromiseAtom, retryPromises),
              trigger: EVENT_LOGIN_MODAL_VISITOR_TRIGGER.RENEW_ACCESS_TOKEN
            })
          });
        }
      }
    }
  } finally {
    set(isRetryingAtom, false);
  }
});
export const loginWithTokenAtom = atom(null, async (get, set) => {
  const azarToken = get(azarTokenAtom)?.accessToken;
  if (get(isRetryingAtom) || !azarToken) {
    return;
  }
  set(isRetryingAtom, true);
  try {
    const response = await loginWithAccessTokenAPI(azarToken);
    if (response.data.result?.accessToken) {
      const userData = get(userDataAtom);
      if (userData) {
        set(userDataAtom, {
          ...userData,
          refreshToken: response.data.result?.refreshToken,
          accessToken: response.data.result?.accessToken
        });
      }
      set(fireRetryPromiseAtom);
    }
  } catch (_e) {
    // TODO: sentry
  }
  set(isRetryingAtom, false);
});
const retryLoginWithCancelDeletionReservationAtom = atom(null, async (get, set, config: AxiosRequestConfig) => {
  const originData = JSON.parse(config.data);
  const response = await rpcAxios<RPCResponse<LoginResponse, RPCThrowableError>>({
    ...config,
    data: {
      ...originData,
      params: [{
        ...originData.params[0],
        cancelDeletionReservation: true
      }]
    }
  });
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  set(handleLoginResultAtom, response);
});
const retrySignUpWithPurgeBeforeSignUpAtom = atom(null, (get, set) => {
  set(purgeBeforeSignUpAtom, true);
  if (get(tryLoginTypeAtom) === LoginType.SMS) {
    set(pushPhoneNumberLoginHistoryAtom, <EmailAddressRegisterStep />);
  } else {
    switch (appConfig.serviceCode) {
      case 'vividi':
        set(showModalAtom, {
          key: ModalType.REGISTER,
          component: VividiRegisterModal
        });
        break;
      case 'azar':
      default:
        set(loginModalTypeAtom, LoginModalType.SOCIAL_REGISTER);
    }
  }
});
export const handleAgeRestrictedLoginAttemptAtom = atom(null, (get, set) => {
  set(closeModalAtom, ModalType.LOGIN);
  set(showModalAtom, {
    key: ModalType.AGE_RESTRICTED,
    component: () => <AgeRestrictedModal reason='registration' />
  });
});
export const handleLoginResultAtom = atom(null, (get, set, response: Partial<Awaited<ReturnType<typeof loginOAuthAPI> | ReturnType<typeof loginEmailAPI> | ReturnType<typeof loginWithPhoneNumberAPI>>>) => {
  const {
    data: {
      result,
      error
    } = {}
  } = response;
  if (result?.accessToken) {
    const storageToken = get(azarTokenAtom);
    const {
      userSignedUp,
      ...azarUser
    } = result;
    let userData = azarUser;
    if (storageToken?.refreshToken) {
      const newTokenExp = jwtDecode<Token>(userData.refreshToken).exp;
      const storageTokenExp = jwtDecode<Token>(storageToken.refreshToken).exp;

      // TODO: 꼭 필요한지, useSession의 webview login 처리 로직 개선 검토
      // NOTE: 기존 토큰이 유효기간이 더 긴 경우 기존 토큰 유지
      if (storageTokenExp > newTokenExp) {
        userData = {
          ...userData,
          ...storageToken
        };
      }
    }
    pushEvent({
      eventType: EVENT_TYPE.WEB,
      eventName: EVENT_NAME.LOGIN_SUCCESS,
      eventParams: {
        method: get(tryLoginTypeAtom)
      },
      options: {
        user_id: azarUser.userId
      }
    });
    set(lastGetLegalUpdatesDateAtom, new Date());
    set(isUserJustSignedUpAtom, !!userSignedUp);
    set(userDataAtom, userData);
    set(lastUserInfoUpdateDateAtom, new Date());
    if (safeWindow?.AzarJs) {
      set(getInventoryAtom);
      set(setServersInfoAtom);
    } else {
      IceServerManager.getInstance().setIceServers(userData.iceServers);
      set(stompBrokerInfoAtom, userData.stompBrokerInfo);
      set(userInventoryAtom, parseRawInventoryItems(userData.inventoryItems));
    }
    set(getVipRatingAtom);
  } else if (error) {
    set(removeTokenAtom);
    if (isDefinedRPCException(error, RPC_ERROR_CODES.BAD_CREDENTIALS)) {
      switch (error.data.throwable.reason) {
        case 'username_not_found':
          /** TODO: 분기를 치지 않는 방향으로 리팩토링할 것
           * 이렇게 serviceCode 를 사용해서 분기하는 코드가 비즈니스 로직에 많아졌을 때 나중에 코드를 분석하기 어려워지는 문제가 있음.
           * 이 때 Azar 에서도 Register 를 별도 모달로 분리한다면 통합이 더 쉬워질 것.
           **/
          switch (appConfig.serviceCode) {
            case 'vividi':
              set(showModalAtom, {
                key: ModalType.REGISTER,
                component: VividiRegisterModal
              });
              break;
            case 'azar':
            default:
              set(loginModalTypeAtom, LoginModalType.SOCIAL_REGISTER);
          }
          break;
        case 'account_deletion_reservation':
          if (!response.config) {
            return;
          }
          set(showModalAtom, {
            key: ModalType.CANCEL_DELETION_RESERVATION,
            component: () => CancelDeletionReservationModal({
              onCancelDeletionReservation: () => {
                set(retryLoginWithCancelDeletionReservationAtom, {
                  ...response.config
                });
              },
              onCreateNewAccount: () => {
                set(retrySignUpWithPurgeBeforeSignUpAtom);
              }
            })
          });
          break;
        default:
          set(closeModalAtom, ModalType.LOGIN);
          set(showModalAtom, {
            key: ModalType.QR,
            component: ErrorAccountsModal
          });
          break;
      }
      return;
    }
    if (isDefinedRPCException(error, RPC_ERROR_CODES.PUNISHED)) {
      set(closeModalAtom, ModalType.LOGIN);
      set(showModalAtom, {
        key: ModalType.LOGIN_SUSPENSION,
        component: () => SuspensionModal({
          punishment: error.data.throwable
        })
      });
      return;
    }
    if (isDefinedRPCException(error, RPC_ERROR_CODES.AZAR_AGE)) {
      set(closeModalAtom, ModalType.LOGIN);
      set(showModalAtom, {
        key: ModalType.ERROR,
        component: () => ErrorAccountsModal({
          errorMessage: 'LOGIN__ERROR_AGE'
        })
      });
      return;
    }
    if (isDefinedRPCException(error, RPC_ERROR_CODES.NOT_KMC_VERIFIED)) {
      // KMC 자체 에러가 아닌 서버 검증 실패 → 로그인 화면 + 에러 토스트
      set(handleNonKmcSelfErrorAtom);
      return;
    }

    // 2차 인증이 필요한 경우
    if (isDefinedRPCException(error, RPC_ERROR_CODES.NOT_VERIFIED) && response.config) {
      set(retryWithSmsVerificationAtom, {
        config: response.config
      });
      return;
    }

    // 디바이스 변경 O, 이메일 등록 X, 인증 X
    if (isDefinedRPCException(error, RPC_ERROR_CODES.DEVICE_TRANSFER)) {
      set(pushPhoneNumberLoginHistoryAtom, <NotExistEmailStep />);
      return;
    }

    // 디바이스 변경 O, 이메일 등록 O, 인증 X
    if (isDefinedRPCException(error, RPC_ERROR_CODES.NOT_EMAIL_VERIFIED) && !error.data.throwable.verified) {
      set(pushPhoneNumberLoginHistoryAtom, <EmailVerificationSelectStep />);
      set(emailVerificationAtom, {
        email: error.data.throwable.userEmail
      });
      return;
    }

    // 디바이스 변경 O, 이메일 등록 O, 인증 O
    if (isDefinedRPCException(error, RPC_ERROR_CODES.NOT_EMAIL_VERIFIED) && error.data.throwable.verified) {
      set(emailVerificationAtom, {
        email: error.data.throwable.userEmail
      });
      set(pushPhoneNumberLoginHistoryAtom, <EmailAddressMatchStep />);
      return;
    }
    if (isDefinedRPCException(error, RPC_ERROR_CODES.AZAR_FEEDBACK)) {
      set(closeModalAtom, ModalType.LOGIN);
      set(showModalAtom, {
        key: ModalType.AZAR_FEEDBACK_DIALOG,
        component: () => <AlertModal>{error.data.throwable.alertBody}</AlertModal>
      });
      return;
    }

    // 기대하지 않은 login 실패 케이스 발생 시, 로깅
    Sentry.captureMessage('Unexpected Login Error', {
      extra: {
        error,
        loginType: get(tryLoginTypeAtom)
      }
    });
    set(showToastAtom, {
      message: 'LOGIN__ERROR',
      type: ToastType.ERROR
    });
  } else {
    if (window.AzarJs) {
      Sentry.captureMessage(`Unexpected LoginResponse, ${JSON.stringify(response.status)}, ${JSON.stringify(response.data)}`);
      alert('Sorry. Please login again');
      window.AzarJs.closePopup();
    } else {
      set(showToastAtom, {
        message: 'LOGIN__ERROR',
        type: ToastType.ERROR
      });
    }
  }
});
export const retryLoginWithPhoneVerifyAtom = atom(null, async (get, set, config: AxiosRequestConfig) => {
  try {
    const response = await rpcAxios<RPCResponse<LoginResponse, RPCThrowableError>>(config);
    set(handleLoginResultAtom, response);
  } catch (_e) {
    // 별도 핸들링 필요하지 않음
  } finally {
    set(closeModalAtom, ModalType.LOGIN);
  }
});
export const loginOAuthAtom = atom(null, async (get, set, params: Omit<LoginOAuthParams, 'method'>) => {
  const socialBrand = get(tryLoginTypeAtom);
  if (!params.accessToken || socialBrand === undefined) {
    set(isLoginLoadingAtom, false);
    return;
  }
  set(isLoginLoadingAtom, true);
  try {
    const response = await loginOAuthAPI({
      method: socialBrand,
      ...params
    });
    set(handleLoginResultAtom, response);
  } catch (error) {
    set(closeModalAtom, ModalType.LOGIN);
    set(showToastAtom, {
      message: 'LOGIN__ERROR',
      type: ToastType.ERROR
    });
    Sentry.captureException(`[LoginOAuth] - ${socialBrand} loginOAuthAPI Error`, {
      extra: {
        error
      }
    });
  } finally {
    set(isLoginLoadingAtom, false);
  }
});
export const loginWithPhoneNumberAtom = atom(null, async (get, set) => {
  try {
    const smsVerificationResponse = get(smsVerificationResponseAtom);
    if (!smsVerificationResponse) {
      Sentry.captureMessage('loginWithPhoneNumberAtom called without smsVerificationResponse');
      return;
    }
    const {
      phoneId,
      phoneToken
    } = smsVerificationResponse;
    const socialToken = get(socialTokenAtom);
    const response = await loginWithPhoneNumberAPI({
      phoneId,
      phoneToken,
      ...(socialToken && 'smsVerification' in socialToken && socialToken.smsVerification ? {
        smsVerification: socialToken.smsVerification
      } : {})
    });
    const {
      data: {
        result: azarUser
      } = {}
    } = response;
    //전화번호가입의 경우 에러메시지가 이메일 입력 모달에 나와야함
    set(handleLoginResultAtom, response);

    // 정상 로그인
    if (azarUser) {
      set(loginModalTypeAtom, LoginModalType.LOGIN_ENTRY);
      set(phoneNumberLoginHistoryAtom, RESET);
      return;
    }
  } catch (e) {
    set(resetRegisterAtom);
    set(closeModalAtom, ModalType.LOGIN);
    Sentry.captureMessage(`LoginSMSAPI Error, ${JSON.stringify(e)}`);
  }
});
export const loginEmailAtom = atom(null, async (get, set, params: LoginEmailParams) => {
  const response = await loginEmailAPI(params);
  set(handleLoginResultAtom, response);
});
export const signupEmailAtom = atom(null, async (get, set, params: Pick<RegisterEmailParams, 'email' | 'password'>) => {
  try {
    const response = await registerEmailAPI({
      birthMonth: 11,
      birthDay: 22,
      birthYear: 1993,
      gender: GENDER.MALE,
      consents: {},
      legalNoticesConfirmed: {},
      // 이메일 로그인은 신규 가입이 불가능하여, purgeBeforeSignUp을 false로 설정
      // @see https://www.notion.so/hpcnt/90-28dce00cd35480fca61fc33f2a068d15?source=copy_link#28fce00cd354808db692fde406e30fb3
      purgeBeforeSignUp: false,
      profileImageChosen: false,
      profileImageSkipped: false,
      ...params
    });
    set(handleLoginResultAtom, response);
    set(userJoinDateAtom, new Date());
  } catch (_e) {
    // 504 handle
  }
});
export const turnstileTokenAtom = atom<string | undefined>(undefined);
export const turnstileWidgetSiteKeyAtom = atom<string | undefined>(undefined);
export const turnstileWidgetOnSuccessAtom = atom(null, async (get, set, {
  token,
  callback
}: {
  token: string;
  callback: () => void | Promise<void>;
}) => {
  if (!token) return;
  set(turnstileTokenAtom, token);
  await callback();
  set(turnstileWidgetSiteKeyAtom, undefined);
  set(turnstileTokenAtom, undefined);
});
export const hasDeviceBeenLoggedInAtom = getAtomWithStorage<boolean>('hasDeviceBeenLoggedIn', false);
export const hasInduceRegisterBeenShownAtom = getAtomWithStorage<boolean>('hasInduceRegisterBeenShown', false);
export const showInduceRegisterModalAtom = atom(null, (get, set) => {
  const hasDeviceBeenLoggedIn = get(hasDeviceBeenLoggedInAtom);
  const hasInduceRegisterBeenShown = get(hasInduceRegisterBeenShownAtom);
  const isEnableBouncePopup = !hasDeviceBeenLoggedIn && !hasInduceRegisterBeenShown;
  if (!isEnableBouncePopup) return;
  setTimeout(() => {
    set(showModalAtom, {
      key: ModalType.INDUCE_REGISTER_MODAL,
      component: () => InduceRegisterModal({
        eventParams: {
          trigger: 'signup_drop'
        }
      })
    });
  }, 2000);
});